import Button from '@jetbrains/ring-ui/components/button/button'
import Dropdown, {Anchor} from '@jetbrains/ring-ui/components/dropdown/dropdown'
import {H1, H3} from '@jetbrains/ring-ui/components/heading/heading'
import Popup from '@jetbrains/ring-ui/components/popup/popup'
import Tooltip from '@jetbrains/ring-ui/components/tooltip/tooltip'
import classNames from 'classnames'
import {
  differenceInSeconds,
  formatDistance,
  formatDistanceToNow,
  isBefore,
  min,
  parseISO,
} from 'date-fns'
import merge from 'lodash/merge'
import plur from 'plur'
import React, {Suspense, useEffect, useMemo} from 'react'
import {graphql, useFragment} from 'react-relay'

import useMyId from '../../../../../hooks/useMyId'
import {usePermission} from '../../../../../hooks/usePermission'
import Routes, {getHref} from '../../../../../routes'
import {getBuildStatusIconFromBuild, getBuildStatusTypeFromBuild} from '../../../../../selectors'
import {restApi} from '../../../../../services/rest'
import {Permission} from '../../../../../types'
import {formatDuration, useDisplayDate} from '../../../../../utils/dateTimeUtils'
import {emptyArray} from '../../../../../utils/empty'
import {subscribeOnRunningBuild} from '../../../../../utils/subscriber'
import Avatar, {Size} from '../../../../common/Avatar/Avatar.container'
import BuildDuration from '../../../../common/BuildDuration/BuildDuration.container'
import {formatBuildNumber} from '../../../../common/BuildNumber/BuildNumber.utils'
import Icon from '../../../../common/Icon/Icon'
import Breadcrumbs from '../../components/common/Breadcrumbs/Breadcrumbs'
import {getPipelineRunBreadcrunbs} from '../../components/common/Breadcrumbs/Breadcrumbs.utils'
import JobIconStatus from '../../components/common/JobIconStatus/JobIconStatus'
import PipelineBranchLabel from '../../components/common/PipelineBranchLabel/PipelineBranchLabel'
import PipelineEditButton from '../../components/common/PipelineEditButton/PipelineEditButton'
import RunButton from '../../components/common/RunButton/RunButton'
import RunStatus from '../../components/common/RunStatus/RunStatus'
import headerStyles from '../../EditPipelinePage/EditPipelineHeader/EditPipelineHeader.css'
import {useJobId} from '../../EditPipelinePage/EditPipelinePage.hooks'
import {usePipelineHeadId, usePipelineId} from '../../hooks/pipeline'
import usePipelineDocumentTitle from '../../hooks/usePipelineDocumentTitle'
import {pipelinesApi} from '../../services/pipelinesApi'
import {usePipelineRunArg} from '../PipelineRunPage.hooks'

import type {PipelineRunHeaderFragment$key} from './__generated__/PipelineRunHeaderFragment.graphql'
import {PipelineNavigation} from './PipelineNavigation/PipelineNavigation'

import styles from './PipelineRunHeader.css'

import {useAppDispatch} from 'src/hooks/react-redux'

const fragment = graphql`
  fragment PipelineRunHeaderFragment on ProjectRest {
    rawId
    ...pipelineHeadFragment
  }
`

type Props = {pipelineKey: PipelineRunHeaderFragment$key | null}
function PipelineRunHeader({pipelineKey}: Props) {
  const arg = usePipelineRunArg()
  const {run, jobs} = restApi.endpoints.getBuildNormalized.useQuery(arg, {
    selectFromResult: ({data}) => {
      const {entities, result} = data ?? {}
      const {builds} = entities ?? {}
      const run = builds?.[result!]
      return {
        run,
        jobs: run?.['snapshot-dependencies']?.build ?? emptyArray,
      }
    },
  })
  const pipelineId = usePipelineId()
  const {name} = pipelinesApi.endpoints.getPipelineById.useQuery(pipelineId, {
    selectFromResult: ({data}) => ({name: data?.settings.name}),
  })
  const {
    id,
    number,
    state,
    branchName,
    defaultBranch,
    triggered,
    startEstimate,
    startDate,
    finishDate,
    queuedDate,
  } = run ?? {}
  const headQueued = parseISO(queuedDate!)
  const reused = useMemo(
    () =>
      jobs
        .filter(job => job.startDate != null && isBefore(parseISO(job.startDate), headQueued))
        .map(job => {
          const to =
            job.finishDate != null ? min([parseISO(job.finishDate), headQueued]) : headQueued
          return {
            job,
            saved: differenceInSeconds(to, parseISO(job.startDate!)),
          }
        }),
    [headQueued, jobs],
  )
  const savedTotal = reused.reduce((acc, {saved}) => acc + saved, 0)
  const isQueued = state === 'queued'
  const runNumber = formatBuildNumber({number}).text
  const runStatusType = getBuildStatusTypeFromBuild(run)
  const triggeredDate = useDisplayDate(triggered?.date, true)
  const myId = useMyId()
  const icon = getBuildStatusIconFromBuild(run, myId)
  const dispatch = useAppDispatch()
  const currentJobId = Number(useJobId())
  const currentBuildTypeId = useMemo(
    () => jobs.find(({id: jobId}) => jobId === currentJobId)?.buildType?.name || '',
    [jobs, currentJobId],
  )
  const pipeline = useFragment(fragment, pipelineKey)
  const headId = usePipelineHeadId(pipeline)

  useEffect(() => {
    if (id != null && state === 'running') {
      return subscribeOnRunningBuild(id, data =>
        dispatch(
          restApi.util.updateQueryData('getBuildNormalized', arg, draft => {
            const draftRun = draft?.entities?.builds?.[draft?.result!]

            merge(draftRun, data)
          }),
        ),
      )
    }
    return undefined
  }, [arg, dispatch, id, state])

  usePipelineDocumentTitle(name)

  const [cancelBuild, {isLoading: isCancelling}] = restApi.endpoints.cancelBuild.useMutation()

  const projectId = pipeline?.rawId
  const canCancel = usePermission(Permission.CANCEL_BUILD, projectId)

  return (
    <div className={styles.header}>
      <div className={styles.breadcrumbs}>
        {name != null && (
          <Breadcrumbs
            path={getPipelineRunBreadcrunbs(pipelineId, name, runNumber, runStatusType)}
          />
        )}
      </div>
      <div className={styles.heading}>
        <div className={styles.titleWrapper}>
          <JobIconStatus
            parallelized={run?.parallelized}
            icon={icon}
            status={runStatusType}
            className={styles.iconStatus}
          />
          <H1 className={classNames(headerStyles.title, styles[runStatusType])}>
            {isQueued ? `In queue` : `Run ${runNumber}`}
          </H1>
        </div>
        <div className={styles.branchAndStatus}>
          <RunStatus {...run} withIcon={false} withLink={false} />
        </div>
        <div className={styles.triggered}>
          {triggered?.user != null && <Avatar user={triggered.user} size={Size.Size20} />}
          {`Triggered by ${triggered?.displayText} at ${triggeredDate} in`}
          <PipelineBranchLabel
            className={styles.branch}
            name={branchName}
            main={defaultBranch}
            withIcon
            noLink
          />
        </div>
      </div>
      <div className={styles.pipelineNavigaion}>
        <PipelineNavigation pipelineHeadKey={pipeline} />
      </div>
      <div className={styles.actions}>
        {state !== 'finished' && (
          <Tooltip
            title={
              canCancel
                ? undefined
                : `You don't have permission to ${state === 'running' ? 'stop' : 'cancel'} this run`
            }
          >
            <Button
              className={styles.button}
              onClick={() => cancelBuild({buildLocator: `id:${id}`, buildCancelRequest: {}})}
              loader={isCancelling}
              disabled={!canCancel}
            >
              {state === 'running' ? 'Stop run' : 'Cancel run'}
            </Button>
          </Tooltip>
        )}
        <Suspense>
          <PipelineEditButton
            className={styles.button}
            to={{
              pathname: getHref(Routes.EDIT_PIPELINE, {id: pipelineId}),
              search: `job=${currentBuildTypeId}`,
            }}
          />
        </Suspense>
        <RunButton
          primary
          headId={headId!}
          className={styles.button}
          branchName={defaultBranch ? undefined : branchName}
        />
      </div>
      <div className={styles.runInformation}>
        <div>
          {state === 'queued' ? (
            startEstimate != null ? (
              `Starts ${formatDistanceToNow(parseISO(startEstimate), {addSuffix: true})}`
            ) : null
          ) : state === 'running' ? (
            <BuildDuration buildId={id!} />
          ) : startDate != null && finishDate != null ? (
            `Duration: ${formatDistance(parseISO(startDate), parseISO(finishDate))}`
          ) : null}
        </div>
        {savedTotal > 0 && (
          <Dropdown
            anchor={
              <Anchor className={styles.optimized}>
                <Icon className={styles.optimizedIcon} name="time" />
                {`Optimized ${formatDuration(savedTotal)}`}
              </Anchor>
            }
          >
            <Popup
              className={styles.reusedPopup}
              directions={[Popup.PopupProps.Directions.BOTTOM_LEFT]}
            >
              <H3>{`${reused.length} ${plur('Job', reused.length)} Reused`}</H3>
              {reused.map(({job, saved}) => (
                <div className={styles.reusedItem} key={job.id}>
                  <JobIconStatus
                    parallelized={job?.parallelized}
                    icon={getBuildStatusIconFromBuild(job, myId)}
                    status={job.status}
                    className={styles.icon}
                  />
                  {job.buildType?.name}
                  <div className={styles.reusedItemInfo}>
                    {`Run ${job.number}, saved ${formatDuration(saved)}`}
                  </div>
                </div>
              ))}
            </Popup>
          </Dropdown>
        )}
      </div>
    </div>
  )
}

export default PipelineRunHeader
